import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import PaymentForm from "./paymentForm";
import { fetchLead, fetchQuote } from "../api";

/**
 * Page atteinte via le lien envoyé en fin de conversation par les chatbots
 * Telegram/WhatsApp (functions/chat/flow.js) : /devis/:leadId. Relit la
 * demande enregistrée, recalcule un devis à jour (même moteur que le site),
 * et permet de payer directement — sans ressaisir départ/destination/date.
 */

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const TRIP_TYPE_LABEL = {
  "aller-simple": "Aller simple",
  "aller-retour": "Aller-retour",
  "mise-a-disposition": "Mise à disposition",
};

const Devis = () => {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [quote, setQuote] = useState(null);
  const [email, setEmail] = useState("");
  const [payment, setPayment] = useState(false);
  const [status, setStatus] = useState({ loading: true, error: null });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const leadData = await fetchLead(leadId);
        if (cancelled) return;
        setLead(leadData);
        setEmail(leadData.contactEmail || "");

        const q = await fetchQuote(leadData.trip);
        if (cancelled) return;
        setQuote(q);
        setStatus({ loading: false, error: null });
      } catch (err) {
        if (!cancelled) setStatus({ loading: false, error: err.message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  if (status.loading) {
    return (
      <section className="section">
        <div className="container">
          <p className="section-lead">Chargement de votre devis…</p>
        </div>
      </section>
    );
  }

  if (status.error || !lead || !quote) {
    return (
      <section className="section">
        <div className="container">
          <h2 className="section-title">Devis introuvable</h2>
          <p className="section-lead">
            {status.error || "Ce lien n'est plus valide."}
          </p>
          <div className="lead-quote-actions">
            <Link to="/#reservation" className="btn btn-outline">
              Faire une nouvelle demande
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const trip = { ...lead.trip, email, leadId: lead.id };

  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">Votre devis</h2>

        <div className="booking-card lead-quote-card">
          <div className="booking-head">
            <h2>Bonjour {lead.contactName || ""} 👋</h2>
            <p>Voici le récapitulatif de votre demande.</p>
          </div>

          {lead.status === "paid" ? (
            <div className="quote">
              <p className="quote-price">✅ Cette réservation est déjà payée.</p>
              <p className="quote-note">Merci ! Vous recevrez la confirmation par email.</p>
            </div>
          ) : (
            <>
              <div className="quote">
                <p>
                  {quote.from} → {quote.to}
                </p>
                <p>
                  {TRIP_TYPE_LABEL[lead.trip.tripType] || lead.trip.tripType} · {lead.trip.date} à{" "}
                  {lead.trip.time}
                </p>
                <p>
                  Distance : <strong>{quote.distanceKm} km</strong> · environ {quote.durationMin} min
                </p>
                <p>
                  Tarif de base ({quote.pricePerKm} €/km) : <strong>{quote.basePrice} €</strong>
                </p>

                {quote.surcharge.percent > 0 && (
                  <p className="quote-surcharge">
                    {quote.surcharge.label} : +{quote.surcharge.percent}% (+{quote.surcharge.amount} €)
                  </p>
                )}

                <p className="quote-price">
                  Total estimé : <strong>{quote.price} €</strong>
                </p>
                <p className="quote-note">Tarif indicatif, recalculé et confirmé au paiement.</p>
              </div>

              {!payment && (
                <form
                  className="booking-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setPayment(true);
                  }}
                >
                  {!lead.contactEmail && (
                    <label className="field">
                      <span>Email pour la confirmation et le reçu</span>
                      <input
                        type="email"
                        placeholder="vous@exemple.fr"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </label>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary btn-block"
                    disabled={!email || !stripePromise}
                  >
                    {stripePromise ? "Payer maintenant" : "Paiement indisponible"}
                  </button>

                  {!stripePromise && (
                    <p className="quote-note">
                      Clé Stripe non configurée — contactez-nous pour finaliser la réservation.
                    </p>
                  )}
                </form>
              )}

              {payment && stripePromise && (
                <Elements stripe={stripePromise}>
                  <PaymentForm trip={trip} estimate={quote.price} />
                </Elements>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default Devis;
